import { Typography, Button, Box, Divider } from "@mui/material";
import React, { useState, useRef } from "react";
import StyledButton, { StyledButtonBlack } from "../StyledButton/StyledButton";
import colors from "../../styles/colors";
import VisibilityIcon from '@mui/icons-material/Visibility';

interface MoveableImageProps {
  src: string;
  alt: string;
  hasbutton?: boolean;
  headline?: string;
  description?: string;
  buttonText?: string;
  link?: string;
  onButtonClick?: () => void;
}

const MoveableImage: React.FC<MoveableImageProps> = ({
  src,
  alt,
  hasbutton: explicitHasButton,
  headline,
  description,
  buttonText,
  link,
  onButtonClick,
}) => {
  // Automatically set hasbutton to true if buttonText is provided
  const hasbutton = explicitHasButton !== undefined ? explicitHasButton : !!buttonText;
  
  const [style, setStyle] = useState({
    transform: "rotateX(0deg) rotateY(0deg)",
  });
  const [showOverlay, setShowOverlay] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (showOverlay) return; // Prevent movement when overlay is shown
    
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Calculate position as percentage of container width/height
    const xPercent = x / rect.width;
    const yPercent = y / rect.height;
    
    // Convert percentage to rotation degrees (from -15 to 15)
    const rotateX = (0.5 - yPercent) * 8; // Invert Y axis for natural feel
    const rotateY = (xPercent - 0.5) * 8;
    
    setStyle({ transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1)` });
  };

  const handleMouseLeave = () => {
    if (!showOverlay) {
      setStyle({ transform: "rotateX(0deg) rotateY(0deg) scale(1)" });
    }
  };

  const handleImageClick = () => {
    if (!hasbutton) {
      if (showOverlay) {
        // Reset to initial state
        setShowOverlay(false);
        setStyle({ transform: "rotateX(0deg) rotateY(0deg) scale(1)" });
      } else {
        // Scale down and show overlay
        setShowOverlay(true);
        setStyle({ transform: "rotateX(0deg) rotateY(0deg) scale(0.90)" });
      }
    }
  };

  return (
    <Box
      ref={containerRef}
      className="hover-container"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleImageClick}
      sx={{
        width: "100%",
        height: "100%",
        maxWidth: "500px",
        perspective: "1000px",
        position: "relative",
        cursor: hasbutton ? "initial" : "pointer",
      }}
    >
      <img
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: style.transform,
          transition: "transform 0.3s ease-out",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        }}
      />

      <Box
      id="overlay-container"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "1rem",
          opacity: showOverlay ? 1 : 0,
          visibility: showOverlay ? "visible" : "hidden",
          transition: "opacity 0.4s ease-in-out, visibility 0.4s ease-in-out",
          pointerEvents: showOverlay ? "auto" : "none",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: "white",
            padding: {
              xs: "0 1rem",
              sm: "0 2rem",
              md: "0 3rem",
              lg: "0 4rem",
              xl: "0 5rem",
            },
            textAlign: "center",
            opacity: showOverlay ? 1 : 0,
            transform: showOverlay ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.4s ease-in-out 0.1s, transform 0.4s ease-in-out 0.1s",
          }}
        >
          {headline}
        </Typography>
        <Divider
        sx={{
          borderColor: colors.darkGray,
          width: "3rem",
          opacity: showOverlay ? 1 : 0,
          transform: showOverlay ? "translateY(0)" : "translateY(25px)",
          transition: "opacity 0.4s ease-in-out 0.15s, transform 0.4s ease-in-out 0.15s",
        }}/>
        <Typography
          variant="body2"
          sx={{
            color: "white",
            textAlign: "center",
            padding: {
              xs: "0 1rem",
              sm: "0 1rem",
              md: "0 1rem",
              lg: "0 2rem",
              xl: "0 3rem",
            },
            opacity: showOverlay ? 1 : 0,
            transform: showOverlay ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.4s ease-in-out 0.2s, transform 0.4s ease-in-out 0.2s",
          }}
        >
          {description}
        </Typography>
        {link && (
          <Typography
            variant="body1"
            sx={{
              color: colors.lightgray,
              textAlign: "center",
              textDecoration: "underline",
              textUnderlineOffset: "0.2em",
              textDecorationThickness: "0.05em",
              cursor: "pointer",
              marginTop: "0.5rem",
              opacity: showOverlay ? 1 : 0,
              transform: showOverlay ? "translateY(0)" : "translateY(35px)",
              transition: "opacity 0.4s ease-in-out 0.25s, transform 0.4s ease-in-out 0.25s",
              "&:hover": {
                color: "white",
              },
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent the overlay from closing
              
              // Check if the link is already a full URL (starts with http:// or https://)
              const url = link.startsWith('http://') || link.startsWith('https://') 
                ? link 
                : `https://${link}`; // Add https:// prefix if it's not already there
              
              window.open(url, '_blank'); // Open the properly formatted URL in a new tab
            }}
          >
            Visit Website
          </Typography>
        )}
      </Box>

      {hasbutton && (
        <StyledButtonBlack
          sx={{
            position: "absolute",
            bottom: "10%",
            left: "50%",
            transform: "translateX(-50%)",
          }}
          className="hover-button"
          onClick={onButtonClick}
        >
          {buttonText}
        </StyledButtonBlack>
      )}
    </Box>
  );
};

export default MoveableImage;
